import { signedFetch } from './signedFetch';

// signedFetch/authenticatedFetch resolve on 4xx/5xx (they only auto-retry auth once),
// so every write must check the status explicitly or failures look successful.
async function writeOrThrow(url: string, options: RequestInit): Promise<Response> {
  const res = await signedFetch(url, options);
  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch { /* ignore */ }
    throw new Error(detail || `Request failed (${res.status} ${res.statusText})`);
  }
  return res;
}

// API Gateway URLs per environment
const API_GATEWAY_URL = window.location.hostname.includes('integ-')
  ? 'https://bm1o4jvrkj.execute-api.us-west-2.amazonaws.com/Beta' // Beta
  : 'https://jop1rjczze.execute-api.us-east-1.amazonaws.com/Prod'; // Prod

// --- Usage analytics ---------------------------------------------------------
// Fire-and-forget event tracking. The backend turns each event into a CloudWatch
// EMF metric (totals/graphs) plus a log line carrying the alias (per-user querying
// in Logs Insights). Best-effort: never awaited by callers, never throws.
export type UsageEventType = 'PageView' | 'CreateCR' | 'CreateCRForSelected';

export async function recordUsageEvent(
  eventType: UsageEventType,
  opts: { alias?: string; pipeline?: string; count?: number } = {},
): Promise<void> {
  try {
    let alias = opts.alias;
    if (!alias) {
      const { getCurrentUserAlias } = await import('./midwayAuth');
      alias = await getCurrentUserAlias().catch(() => '');
    }
    await signedFetch(`${API_GATEWAY_URL}/usage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventType, alias: alias || '', pipeline: opts.pipeline || '', count: opts.count ?? 1 }),
    });
  } catch {
    /* analytics is best-effort — swallow everything so tracking never breaks the UI */
  }
}

export interface MissingMetric {
  name: string;
  priority: string;
  tag: string;
  silent?: string;
  what?: string;
  why?: string;
  current?: string;
  where?: string;
  implement?: string;
  impact?: string;
  section?: string; // 'service' | 'dependency' | 'auto-rollback'
  type?: string; // 'individual' | 'derived'
  crLink?: string;
}

export interface MetricsResponse {
  pipeline: string;
  metrics: MissingMetric[];
  feedback: Record<string, string>;        // metricName -> 'helpful' | 'not_helpful'
  feedbackReasons?: Record<string, string>; // metricName -> dislike reason
  autoRollbackAnalysis?: any;
}

// feedback.json values may be a legacy string ('helpful') or the current object { helpful, reason }.
function normalizeFeedback(raw: any): { feedback: Record<string, string>; feedbackReasons: Record<string, string> } {
  const feedback: Record<string, string> = {};
  const feedbackReasons: Record<string, string> = {};
  if (raw && typeof raw === 'object') {
    for (const [name, val] of Object.entries(raw)) {
      if (typeof val === 'string') {
        feedback[name] = val;
      } else if (val && typeof val === 'object') {
        const v = val as { helpful?: string; reason?: string };
        if (v.helpful) feedback[name] = v.helpful;
        if (v.reason) feedbackReasons[name] = v.reason;
      }
    }
  }
  return { feedback, feedbackReasons };
}

export async function listPipelines(): Promise<string[]> {
  const res = await signedFetch(`${API_GATEWAY_URL}/pipelines`);
  const data = await res.json();
  return data.pipelines || [];
}

export async function getMetrics(pipeline: string): Promise<MetricsResponse> {
  const res = await signedFetch(`${API_GATEWAY_URL}/metrics/${encodeURIComponent(pipeline)}`);
  const data = await res.json();
  // Fetch live feedback from API
  let feedback: Record<string, string> = {};
  let feedbackReasons: Record<string, string> = {};
  try {
    const fbRes = await signedFetch(`${API_GATEWAY_URL}/feedback/${encodeURIComponent(pipeline)}`);
    if (fbRes.ok) {
      const fbData = await fbRes.json();
      const raw = fbData.feedbackJson ? JSON.parse(fbData.feedbackJson) : fbData;
      ({ feedback, feedbackReasons } = normalizeFeedback(raw));
    }
  } catch {}
  return {
    pipeline: data.pipeline,
    metrics: (() => {
      const parsed = typeof data.metricsJson === 'string' ? JSON.parse(data.metricsJson) : (data.metrics || []);
      // Handle both flat array and {metrics: [...], autoRollbackAnalysis: {...}} formats
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.metrics)) return parsed.metrics;
      return [];
    })(),
    autoRollbackAnalysis: (() => {
      const parsed = typeof data.metricsJson === 'string' ? JSON.parse(data.metricsJson) : data;
      if (parsed && parsed.autoRollbackAnalysis) return parsed.autoRollbackAnalysis;
      return null;
    })(),
    feedback,
    feedbackReasons,
  };
}

export async function submitFeedback(pipeline: string, metricName: string, helpful: string, reason?: string): Promise<void> {
  await writeOrThrow(`${API_GATEWAY_URL}/feedback/${encodeURIComponent(pipeline)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ metricName, helpful, ...(reason ? { reason } : {}) }),
  });
}

const REQUESTS_API = `${API_GATEWAY_URL}/requests`;

export interface AnalysisRequest {
  pipeline: string;
  requestedBy: string;
  status: string;
  requestedAt: string;
}

export async function submitAnalysisRequest(pipeline: string, requestedBy: string): Promise<void> {
  await writeOrThrow(REQUESTS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline, requestedBy }),
  });
}

export async function getAnalysisRequests(): Promise<AnalysisRequest[]> {
  try {
    const res = await signedFetch(REQUESTS_API);
    if (!res.ok) return [];
    const data = await res.json();
    return data.requests || [];
  } catch { return []; }
}

export async function updateRequestStatus(pipeline: string, requestedAt: string, status: string): Promise<void> {
  await writeOrThrow(REQUESTS_API, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline, requestedAt, status }),
  });
}

export async function deleteAnalysisRequest(pipeline: string, requestedAt: string): Promise<void> {
  const params = new URLSearchParams({ pipeline, requestedAt });
  await writeOrThrow(`${REQUESTS_API}?${params.toString()}`, {
    method: 'DELETE',
  });
}

// --- Customer-Added Business Metrics (review workflow) ---

export interface CustomMetric {
  id: string;
  pipeline: string;
  metricName: string;
  metricLink: string;
  justification: string;
  addedBy: string;
  addedAt: string;
  status: string; // 'pending' | 'approved' | 'rejected'
  reviewedBy?: string;
  reviewedAt?: string;
  modifiedBy?: string;
  modifiedAt?: string;
  deleted?: string;
  deletedBy?: string;
  deletedAt?: string;
  approver1?: string;
  approver1Status?: string;
  approver1At?: string;
  approver2?: string;
  approver2Status?: string;
  approver2At?: string;
}

const CUSTOM_METRICS_API = (pipeline: string) =>
  `${API_GATEWAY_URL}/custom-metrics/${encodeURIComponent(pipeline)}`;

export async function getCustomMetrics(pipeline: string): Promise<CustomMetric[]> {
  try {
    const res = await signedFetch(CUSTOM_METRICS_API(pipeline));
    if (!res.ok) return [];
    const data = await res.json();
    return data.customMetrics || [];
  } catch { return []; }
}

export async function submitCustomMetric(
  pipeline: string,
  entry: { metricName: string; metricLink: string; justification: string; addedBy: string; approver2: string },
): Promise<void> {
  await writeOrThrow(CUSTOM_METRICS_API(pipeline), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });
}

// Approver 1 for a pipeline: the owner, or the owner's manager if the submitter is the owner.
export async function getApprover1(pipeline: string, submitter: string): Promise<string> {
  try {
    const params = new URLSearchParams({ submitter });
    const res = await signedFetch(`${API_GATEWAY_URL}/approver1/${encodeURIComponent(pipeline)}?${params.toString()}`);
    if (!res.ok) return '';
    const data = await res.json();
    return data.approver1 || '';
  } catch { return ''; }
}

export async function reviewCustomMetric(
  pipeline: string,
  id: string,
  status: 'approved' | 'rejected',
  reviewedBy: string,
): Promise<void> {
  await writeOrThrow(CUSTOM_METRICS_API(pipeline), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, status, reviewedBy }),
  });
}

export async function editCustomMetric(
  pipeline: string,
  id: string,
  entry: { metricName: string; metricLink: string; justification: string; modifiedBy: string },
): Promise<void> {
  await writeOrThrow(`${CUSTOM_METRICS_API(pipeline)}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });
}

// --- Org hierarchy (landing view) ---

export interface OrgOwnedPipeline { pipeline: string; scanned: boolean; businessMetrics: boolean; scannedDate?: string; recommendations?: number }
export interface OrgNode {
  alias: string;
  manager?: string | null;
  directReports: string[];
  total: number;
  scanned: number;
  withBusinessMetrics: number;
  missingBusinessMetrics: number;
  recommendations?: number; // recommended metrics across the subtree's scanned pipelines
  ownedPipelines: OrgOwnedPipeline[];
}
export interface OrgHierarchy {
  focus: string;
  root: string;
  org: string;
  tier: string;
  orgs: string[];
  tiers: string[];
  nodes: Record<string, OrgNode>;
}

export async function getOrgHierarchy(org?: string, tier?: string, alias?: string): Promise<OrgHierarchy | null> {
  const params = new URLSearchParams();
  if (org) params.set('org', org);
  if (tier) params.set('tier', tier);
  if (alias) params.set('alias', alias);
  try {
    const res = await signedFetch(`${API_GATEWAY_URL}/org-hierarchy?${params.toString()}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.hierarchyJson ? JSON.parse(data.hierarchyJson) : null;
  } catch { return null; }
}

// Global review queue — pending customer-added metrics across all pipelines.
export async function getPendingApprovals(): Promise<CustomMetric[]> {
  try {
    const res = await signedFetch(`${API_GATEWAY_URL}/pending-approvals`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.customMetrics || [];
  } catch { return []; }
}

export async function deleteCustomMetric(pipeline: string, id: string, deletedBy: string): Promise<void> {
  const params = new URLSearchParams({ deletedBy });
  await writeOrThrow(`${CUSTOM_METRICS_API(pipeline)}/${encodeURIComponent(id)}?${params.toString()}`, {
    method: 'DELETE',
  });
}

// --- Responses to recommended auto-rollback metrics ---

export type RecommendationDisposition = 'will-implement' | 'not-critical' | 'have-own';

export interface RecommendationResponse {
  metricName: string;
  disposition: RecommendationDisposition | string;
  eta?: string;
  note?: string;
  reason?: string;
  respondedBy: string;
  respondedAt: string;
  modifiedBy?: string;
  modifiedAt?: string;
}

const REC_RESPONSES_API = (pipeline: string) =>
  `${API_GATEWAY_URL}/recommendation-responses/${encodeURIComponent(pipeline)}`;

export async function getRecommendationResponses(pipeline: string): Promise<RecommendationResponse[]> {
  try {
    const res = await signedFetch(REC_RESPONSES_API(pipeline));
    if (!res.ok) return [];
    const data = await res.json();
    return data.responses || [];
  } catch { return []; }
}

export async function submitRecommendationResponse(
  pipeline: string,
  entry: { metricName: string; disposition: RecommendationDisposition; eta?: string; note?: string; reason?: string; respondedBy: string },
): Promise<void> {
  await writeOrThrow(REC_RESPONSES_API(pipeline), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });
}
